<script setup lang="ts">
const featureImg = '/images/editorial/card-feature.png'
const blogImg = '/images/editorial/card-blog.png'
const productImg = '/images/products/card-product.png'

type CardType = 'Feature' | 'Testimonial' | 'Blog' | 'Product-short' | 'Product-full'

const props = withDefaults(defineProps<{
  type?: CardType
  categories?: string
  title?: string
  shortDescription?: string
  image?: string
  slug?: string
  price?: string
  tags?: string[]
  dark?: boolean
  animated?: boolean
  visible?: boolean
  elevated?: boolean
  tinted?: boolean
}>(), {
  type: 'Feature',
  categories: 'TRAVEL',
  title: 'Desert Roads &\nOpen Sky',
  shortDescription: 'A quiet journey through place, material, and the beauty found in slowness.',
  image: '',
  slug: '#',
  price: '',
  dark: false,
  animated: false,
  visible: false,
  elevated: false,
  tinted: false,
})

const isComingSoon = computed(() => props.tags?.includes('coming-soon') ?? false)
const resolvedFeatureImg = computed(() => props.image || featureImg)
const resolvedBlogImg = computed(() => props.image || blogImg)
const resolvedProductImg = computed(() => props.image || productImg)
const resolvedSlug = computed(() => props.slug || '#')
</script>

<template>
  <!-- Feature Card -->
  <NuxtLink
    v-if="type === 'Feature'"
    :to="resolvedSlug"
    :class="[
      'group flex flex-col items-start w-full',
      animated && 'transition-all duration-500',
      animated && !visible && 'opacity-0 translate-y-6',
      animated && visible && 'opacity-100 translate-y-0',
      elevated && ' hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300',
    ]"
  >
    <div :class="['relative aspect-[3/4] w-full overflow-hidden', tinted && 'card-image-tinted']">
      <img alt="" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" :src="resolvedFeatureImg" />
    </div>
    <div class="flex flex-col gap-1 items-start overflow-hidden p-4 md:p-2 w-full">
      <p :class="[
        'font-label font-light text-xs leading-4 tracking-wider whitespace-nowrap',
        tinted ? 'text-brand  ' : 'text-brand',
      ]">
        {{ categories }}
      </p>
      <div class="w-full space-y-1">
        <p class="font-serif font-normal text-lg md:text-xl leading-6 md:leading-8 text-dark min-w-full w-min whitespace-pre-line line-clamp-3">{{ title }}</p>
        <div class="relative overflow-hidden max-h-12 min-w-full w-min">
          <p class="font-sans font-light text-xs text-gray-400">
            {{ shortDescription }}
          </p>
          <div class="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-page to-transparent pointer-events-none transition-opacity duration-300 group-hover:opacity-0" />
        </div>
      </div>
    </div>
  </NuxtLink>

  <!-- Testimonial Card -->
  <article
    v-else-if="type === 'Testimonial'"
    class="flex flex-col items-start bg-surface border border-stroke overflow-hidden shadow-[0px_2px_8px_0px_rgba(0,0,0,0.08)]"
  >
    <div class="flex h-60 items-center justify-center bg-raised w-[400px] overflow-hidden shrink-0">
      <p class="font-label font-light text-xs text-muted whitespace-nowrap">[ Image ]</p>
    </div>
    <div class="flex flex-col gap-2 items-start overflow-hidden p-6 w-full">
      <p class="font-label font-light text-xs leading-4 tracking-wider text-brand whitespace-nowrap">GOODS</p>
      <p class="font-serif font-normal text-2xl leading-6 text-dark whitespace-nowrap">Desert Roads &amp; Open Sky</p>
      <p class="font-sans font-light text-sm leading-5 text-secondary min-w-full w-min">
        {{ shortDescription }}
      </p>
      <AppButton type="Ghost" size="Small" label="Read More" :showIconRight="true" />
    </div>
  </article>

  <!-- Blog Card -->
  <article
    v-else-if="type === 'Blog'"
    class="flex flex-col items-start overflow-hidden shadow-[0px_2px_8px_0px_rgba(0,0,0,0.08)] w-[400px]"
  >
    <div class="relative h-60 w-full overflow-hidden">
      <img alt="" class="absolute inset-0 w-full h-full object-cover" :src="blogImg" />
    </div>
    <div class="flex flex-col gap-2 items-start overflow-hidden p-6 w-full">
      <p class="font-label font-light text-xs leading-4 tracking-wider text-brand whitespace-nowrap">TRAVEL</p>
      <div class="font-serif font-normal text-2xl text-dark whitespace-nowrap">
        <p class="leading-6 mb-0">Highway 99</p>
        <p class="leading-6">at the Golden Hour</p>
      </div>
      <AppButton type="Ghost" size="Small" label="By James O." :showIconRight="true" />
    </div>
  </article>

  <!-- Product Short Card -->
  <NuxtLink
    v-else-if="type === 'Product-short'"
    :to="resolvedSlug"
    :class="[
      'group flex flex-col items-start overflow-hidden w-full',
      animated && 'transition-all duration-500',
      animated && !visible && 'opacity-0 translate-y-6',
      animated && visible && 'opacity-100 translate-y-0',
      elevated && 'hover:-translate-y-1 transition-all duration-300',
    ]"
  >
    <div class="relative w-full overflow-hidden" style="aspect-ratio: 320/320;">
      <img alt="" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" :src="resolvedProductImg" />
    </div>
    <div class="flex flex-col gap-2 items-start overflow-hidden py-4 w-full">
      <div class="flex items-end justify-between w-full whitespace-nowrap">
        <p :class="['font-serif font-normal text-lg md:text-2xl leading-6 tracking-wide', dark ? 'text-white' : 'text-dark']">{{ title }}</p>
        <p v-if="price" class="font-sans font-light text-base md:text-lg leading-7 text-brand">{{ price }}</p>
      </div>
    </div>
  </NuxtLink>

  <!-- Product Full Card -->
  <NuxtLink
    v-else-if="type === 'Product-full'"
    :to="resolvedSlug"
    class="group flex flex-col items-start overflow-hidden w-full"
  >
    <div class="relative aspect-square w-full overflow-hidden bg-white">
      <span v-if="isComingSoon" class="absolute top-3 left-3 z-10 font-label font-light text-sm uppercase tracking-widest bg-dark text-brand-light px-3 py-1">
        Coming Soon
      </span>
      <img alt="" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" :src="resolvedProductImg" />
    </div>
    <div class="flex flex-col gap-1 items-start overflow-hidden p-4 md:p-4 w-full">
      <p class="font-label font-light text-xs leading-4 tracking-wider text-brand">{{ categories }}</p>
      <div class="flex flex-col items-start text-dark gap-1">
        <p class="font-serif font-normal text-xl md:text-lg leading-6">{{ title }}</p>
        <p v-if="price" class="font-sans font-light text-lg md:text-sm leading-8">{{ price }}</p>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped>
/* Warm tint overlay on hover — only when tinted prop is set */
.card-image-tinted::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(200, 168, 122, 0.10);
  opacity: 0;
  transition: opacity 300ms ease;
  pointer-events: none;
}

.group:hover .card-image-tinted::after {
  opacity: 1;
}
</style>
